'use client';

import { FileText, Image as ImageIcon, Music, Play, X } from 'lucide-react';
import type { StoryAsset } from '../../types';

interface ShotAssetChipProps {
  asset: StoryAsset;
  onRemove?: () => void;
  onClick?: () => void;
  /**
   * thumbnail：方形小图，沿用旧版资产面板的紧凑样式
   * preview：在分镜引用资源区使用，图片/视频尽量展示全貌，便于辨认
   */
  variant?: 'thumbnail' | 'preview';
  size?: 'sm' | 'md';
}

// previewUrl 还没拉到时用类型对应的 lucide 图标占位，避免所有未加载图都
// 显示成 FileText、看上去像不可预览的文件。
const ICON_BY_TYPE = {
  image: ImageIcon,
  video: Play,
  audio: Music,
  file: FileText,
} as const;

export const ShotAssetChip = ({ asset, onRemove, onClick, variant = 'thumbnail', size = 'md' }: ShotAssetChipProps) => {
  const Icon = ICON_BY_TYPE[asset.type ?? 'file'];
  const isPreview = variant === 'preview';
  const isImage = asset.type === 'image' && !!asset.previewUrl;
  const isVideo = asset.type === 'video' && !!asset.previewUrl;

  const containerClass = isPreview
    ? 'group relative w-full overflow-hidden rounded-12 border border-system-white08 bg-system-white08'
    : `group relative shrink-0 overflow-hidden rounded-12 border border-system-white08 bg-system-white08 ${
        size === 'sm' ? 'h-16 w-16' : 'h-20 w-20'
      }`;

  const mediaWrapperClass = isPreview
    ? 'relative flex aspect-video w-full items-center justify-center bg-black/40'
    : 'flex size-full items-center justify-center bg-black/20';

  const renderMedia = () => {
    if (isImage) {
      return <img src={asset.previewUrl} alt={asset.name} className="size-full object-contain" />;
    }
    if (isVideo) {
      return (
        <>
          <video className="size-full object-contain" src={asset.previewUrl} preload="metadata" muted playsInline />
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/20 text-system-white">
            <Play className="size-5 fill-current" />
          </div>
        </>
      );
    }
    // thumbnail 模式下外层会单独展示文件名（避免重复展示）；
    // preview 模式下底部信息条已经写了 name，也不再重复。
    return (
      <div className="flex size-full items-center justify-center px-2 text-system-white48">
        {Icon ? <Icon className="size-6" /> : <FileText className="size-6" />}
      </div>
    );
  };

  return (
    <div className={containerClass}>
      <button
        type="button"
        // thumbnail 模式下需要撑满父容器高度，否则 icon/缩略图会贴顶看起来不居中。
        // preview 模式由内部 aspect-video 控制高度，不需要 size-full。
        className={isPreview ? 'block w-full cursor-pointer text-left' : 'block size-full cursor-pointer text-left'}
        onClick={onClick}
        title={asset.path}
      >
        <div className={mediaWrapperClass}>{renderMedia()}</div>
        {isPreview && (
          <div className="border-system-white08 border-t bg-black/30 px-3 py-1.5">
            <div className="line-clamp-1 text-system-white text-xs" title={asset.name}>
              {asset.name}
            </div>
            {asset.path && (
              <div className="line-clamp-1 text-[10px] text-system-white48" title={asset.path}>
                {asset.path}
              </div>
            )}
          </div>
        )}
      </button>

      {onRemove && (
        <button
          type="button"
          aria-label="remove"
          className="absolute top-1 right-1 flex size-5 items-center justify-center rounded-full bg-black/60 text-system-white opacity-0 transition hover:bg-black/80 group-hover:opacity-100"
          onClick={(event) => {
            event.stopPropagation();
            onRemove();
          }}
        >
          <X className="size-3" />
        </button>
      )}
    </div>
  );
};
